// Razorpay Payment Configuration for Admin Dashboard
// 
// IMPORTANT: For PRODUCTION, you need to:
// 1. Create a backend API endpoint to create Razorpay orders
// 2. Replace the mock order creation in RazorpayService.ts with real API calls
// 3. Implement proper signature verification on backend
// 
// Current setup is for DEVELOPMENT/TESTING with direct payments (no order_id)

export const RAZORPAY_CONFIG = {
  // Test Keys - For testing
  test: {
    keyId: 'rzp_test_Rh5AXoduQDeno1',
    keySecret: 'sTbF5yWsI16MxpjBrql2EKmj',
    environment: 'test'
  },
  
  // Production Keys - Live Razorpay credentials
  production: {
    keyId: 'rzp_live_RkMmphV9lefW0Y',
    keySecret: 'd18kAxawo4jMLcVW6hNPb23U',
    environment: 'production'
  },

  // Payment options configuration
  paymentOptions: {
    currency: 'INR',
    receipt_prefix: 'KBR_ADMIN_',
    
    // Order configuration
    orderDefaults: {
      currency: 'INR',
      partial_payment: false,
      payment_capture: 1  // Auto-capture payment (1 = auto, 0 = manual)
    },

    // Checkout configuration for web
    checkoutOptions: {
      theme: {
        color: '#2563EB' // KBR Blue theme color
      },
      modal: {
        backdrop_close: false,
        escape: false,
        handleback: false,
        confirm_close: true,
        animation: true
      },
      retry: {
        enabled: true,
        max_count: 3
      }
    },

    // Allowed payment methods
    methods: {
      netbanking: true,
      card: true,
      wallet: true,
      upi: true,
      paylater: true,
      emi: false // Disable EMI for medical payments
    },

    // Hospital-specific settings
    hospitalInfo: {
      name: 'KBR Trinity Private Limited',
      // logo: 'https://your-hospital-logo-url.com/logo.png', // Temporarily disabled to avoid 404 errors
      description: 'Medical Services Payment'
    }
  },

  // Transaction types
  transactionTypes: {
    CONSULTATION: 'consultation',
    ADMISSION: 'admission',
    TESTS: 'tests',
    PHARMACY: 'pharmacy',
    EMERGENCY: 'emergency',
    SURGERY: 'surgery',
    ROOM_CHARGES: 'room_charges',
    MEDICINE: 'medicine',
    TREATMENT: 'treatment',
    REGISTRATION: 'registration',
    OTHER: 'other'
  },

  // Payment status mappings
  paymentStatus: {
    CREATED: 'created',
    AUTHORIZED: 'authorized',
    CAPTURED: 'captured',
    REFUNDED: 'refunded',
    FAILED: 'failed',
    PENDING: 'pending'
  }
};

// Get configuration based on environment
export const getRazorpayConfig = (environment: 'test' | 'production' = 'production') => {
  const isProduction = environment === 'production';
  const config = isProduction ? RAZORPAY_CONFIG.production : RAZORPAY_CONFIG.test;
  
  return {
    ...config,
    ...RAZORPAY_CONFIG.paymentOptions,
    isProduction
  };
};

// Get current environment configuration - ALWAYS USE PRODUCTION
export const getCurrentRazorpayConfig = () => {
  // Force production mode for live payments
  const environment = 'production';
  return getRazorpayConfig(environment);
};

// Validate Razorpay keys
export const validateRazorpayKeys = (keyId: string, keySecret: string) => {
  if (!keyId || !keySecret) {
    return { valid: false, error: 'Missing Razorpay keys' };
  }
  
  if (!keyId.startsWith('rzp_')) {
    return { valid: false, error: 'Invalid key ID format' };
  }
  
  if (keySecret.length < 20) {
    return { valid: false, error: 'Invalid key secret format' };
  }
  
  return { valid: true };
};

// Generate receipt ID
export const generateReceiptId = (type: string = 'general', patientId: string | null = null) => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  const prefix = RAZORPAY_CONFIG.paymentOptions.receipt_prefix;
  
  if (patientId) {
    return `${prefix}${type.toUpperCase()}_${patientId}_${timestamp}_${random}`;
  }
  
  return `${prefix}${type.toUpperCase()}_${timestamp}_${random}`;
};

export default RAZORPAY_CONFIG;
